import { styled as muiStyled } from '@mui/material/styles'
import I18n from 'i18n-js'
import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import styled from 'styled-components'

import useTracking from '@src/components/shared/Tracking/useTracking'

import Button from '../../../shared/Button'
import LegacySvg from '../../../shared/LegacySvg'

const Wrapper = muiStyled(Button)(
  ({ theme, buttonState, showUnableToActivate }) => ({
    display: 'flex',
    borderRadius: 0,
    alignItems: 'center',
    justifyContent: 'center',
    textTransform: 'uppercase',
    ...(showUnableToActivate
      ? { tabIndex: -1, cursor: 'not-allowed', outline: 0 }
      : {}),
    ...([
      states.availableRetailerPaidAd,
      states.availablePaidAd,
      states.availableOrganicCampaign,
      states.availableLocalAdCampaign,
    ].includes(buttonState)
      ? {
          'background': theme.palette.pbxxCyan.main,
          'color': theme.palette.common.white,
          '&:hover': {
            color: theme.palette.common.white,
            background: theme.palette.pbxxCyan.dark,
          },
        }
      : {}),
  }),
)

const Dot = muiStyled('span')(({ theme, isActivated }) => ({
  display: 'inline-block',
  height: '16px',
  width: '16px',
  borderRadius: '50%',
  marginRight: '8px',

  ...(isActivated
    ? { background: theme.palette.success.light, opacity: 0.75 }
    : { border: '1px solid gray', background: theme.palette.common.white }),
}))

const Lock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 16px;
  width: 16px;
  margin-right: 8px;
`

const UnavailableText = muiStyled('span')(({ theme }) => ({
  color: theme.palette.grey[300],
}))

const events = {
  paidAd: 'Paid Ad Campaign View Link Clicked',
  paidAdExpired: 'Expired Paid Ad Campaign View Link Clicked',
  organic: 'Organic Campaign View Link Clicked',
  localAd: 'Local Ad Campaign View Link Clicked',
}

const states = {
  activatedPaidAd: 'activatedPaidAd',
  availablePaidAd: 'availablePaidAd',
  availableRetailerPaidAd: 'availableRetailerPaidAd',
  expiredPaidAd: 'expiredPaidAd',
  activatedOrganicCampaign: 'activatedOrganicCampaign',
  availableOrganicCampaign: 'availableOrganicCampaign',
  activatedLocalAdCampaign: 'activatedLocalAdCampaign',
  availableLocalAdCampaign: 'availableLocalAdCampaign',
}

const calculateButtonState = ({ type, startDate, isActivated, paidType }) => {
  if (type === 'paidAd') {
    if (isActivated) {
      return states.activatedPaidAd
    } else {
      if (startDate < Date.now()) {
        return states.expiredPaidAd
      } else {
        return paidType === 'retailer_funded'
          ? states.availableRetailerPaidAd
          : states.availablePaidAd
      }
    }
  } else if (type === 'localAd') {
    if (isActivated) {
      return states.activatedLocalAdCampaign
    } else {
      return states.availableLocalAdCampaign
    }
  } else {
    if (isActivated) {
      return states.activatedOrganicCampaign
    } else {
      return states.availableOrganicCampaign
    }
  }
}

const getTrackingEvent = (buttonState) => {
  switch (buttonState) {
    case states.availableRetailerPaidAd:
    case states.availablePaidAd:
    case states.activatedPaidAd:
      return events.paidAd
    case states.expiredPaidAd:
      return events.paidAdExpired
    case states.availableOrganicCampaign:
    case states.activatedOrganicCampaign:
      return events.organic
    case states.activatedLocalAdCampaign:
    case states.availableLocalAdCampaign:
      return events.localAd
    default:
      return undefined
  }
}

const renderText = (buttonState) => {
  switch (buttonState) {
    case states.availablePaidAd:
      return (
        <>
          <Dot />
          {I18n.t('content.viewClaimAdFunds')}
        </>
      )
    case states.availableRetailerPaidAd:
      return (
        <>
          <Dot />
          {I18n.t('content.viewAndSpendFunds')}
        </>
      )
    case states.activatedPaidAd:
      return (
        <>
          <Dot isActivated />
          {I18n.t('content.viewPremiumCampaign')}
        </>
      )
    case states.expiredPaidAd:
      return (
        <>
          <Lock>
            <LegacySvg height={'16px'} symbol={'lock'} width={'16px'} />
          </Lock>
          <UnavailableText>
            {I18n.t('content.unavailableForActivation')}
          </UnavailableText>
        </>
      )
    case states.availableOrganicCampaign:
    case states.availableLocalAdCampaign:
      return (
        <>
          <Dot />
          {I18n.t('content.activateCampaign')}
        </>
      )
    case states.activatedOrganicCampaign:
    case states.activatedLocalAdCampaign:
      return (
        <>
          <Dot isActivated />
          {I18n.t('content.viewCampaign')}
        </>
      )
    default:
      return null
  }
}

const CampaignCardCTA = (props) => {
  const { id, type, startDate, isActivated, isNew } = props
  const trackingData = { campaignId: id }
  const [buttonState, updateButtonState] = useState(calculateButtonState(props))
  const [isActivating, updateIsActivating] = useState(false)
  const { track } = useTracking()

  const onClick = (event) => {
    track({
      event: 'Click',
      data: {
        type: 'Clicked on campaign card',
        isNew,
        campaignId: id,
      },
    })
    if (buttonState !== states.expiredPaidAd) {
      if (props.type === 'contentDiscovery') {
        updateIsActivating(true)
      }
      props.viewOrActivate(event)
    }
  }

  useEffect(
    () => updateButtonState(calculateButtonState(props)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type, startDate, isActivated],
  )
  return (
    <Wrapper
      buttonState={buttonState}
      buttonStyle={'text'}
      data-testid={`${buttonState}Button`}
      disabled={buttonState === states.expiredPaidAd}
      loading={isActivating ? true : undefined}
      onClick={(event) => onClick(event)}
      showUnableToActivate={buttonState === states.expiredPaidAd}
      trackingData={trackingData}
      trackingEvent={getTrackingEvent(buttonState)}
    >
      {renderText(buttonState)}
    </Wrapper>
  )
}

CampaignCardCTA.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isActivated: PropTypes.bool,
  isNew: PropTypes.bool,
  startDate: PropTypes.string,
  type: PropTypes.oneOf(['paidAd', 'localAd', 'campaign', 'contentDiscovery']),
  viewOrActivate: PropTypes.func,
}

CampaignCardCTA.defaultProps = {}

export default CampaignCardCTA
